/**
 * FILE: apps/hub/src/hooks/mutations/useStaffRoleMutations.ts
 * PURPOSE: Shared TanStack Query mutation hooks for Staff & Roles.
 */

import { useMutationWithToast } from '@/hooks/useMutationWithToast'
import { QK } from '@/lib/queryKeys'
import type { ActivityActor } from '@fintree/supabase/shared'
import type { Permission } from '@fintree/supabase/roles'
import { createCustomRole, deleteCustomRole, updateCustomRole } from '@fintree/supabase/roles'
import { createStaffMember, deactivateStaff, updateStaffCustomRole } from '@fintree/supabase/staff'
import type { AddStaffForm } from '@/pages/settings/settingsTypes'

/* ───────── Roles ───────── */

export function useCreateCustomRole(actor: ActivityActor, options?: { onSuccess?: () => void }) {
  return useMutationWithToast({
    mutationFn: (values: { name: string; description: string; permissions: Permission[] }) =>
      createCustomRole(values, actor),
    successMessage: 'Role created',
    invalidateKeys: [() => QK.customRoles()],
    onSuccess: options?.onSuccess,
  })
}

export function useUpdateCustomRole(actor: ActivityActor, options?: { onSuccess?: () => void }) {
  return useMutationWithToast({
    mutationFn: (payload: { id: string; values: { name: string; description: string; permissions: Permission[] } }) =>
      updateCustomRole(payload.id, payload.values, actor),
    successMessage: 'Role updated',
    invalidateKeys: [() => QK.customRoles()],
    onSuccess: options?.onSuccess,
  })
}

export function useDeleteCustomRole(actor: ActivityActor, options?: { onSuccess?: () => void }) {
  return useMutationWithToast({
    mutationFn: (id: string) => deleteCustomRole(id, actor),
    successMessage: 'Role deleted',
    invalidateKeys: [() => QK.customRoles(), () => QK.staffMembers()],
    onSuccess: options?.onSuccess,
  })
}

/* ───────── Staff ───────── */

export function useCreateStaffMember(
  actor: ActivityActor,
  options?: { onSuccess?: (data: Awaited<ReturnType<typeof createStaffMember>>) => void }
) {
  return useMutationWithToast({
    mutationFn: (values: AddStaffForm) =>
      createStaffMember(
        { name: values.name, email: values.email, customRoleId: values.customRoleId },
        actor
      ),
    successMessage: 'Staff account created',
    invalidateKeys: [() => QK.staffMembers()],
    onSuccess: (data) => options?.onSuccess?.(data),
  })
}

export function useUpdateStaffCustomRole(actor: ActivityActor, options?: { onSuccess?: () => void }) {
  return useMutationWithToast({
    mutationFn: (payload: { id: string; customRoleId: string }) =>
      updateStaffCustomRole(payload.id, payload.customRoleId, actor),
    successMessage: 'Role updated',
    invalidateKeys: [() => QK.staffMembers()],
    onSuccess: options?.onSuccess,
  })
}

export function useDeactivateStaff(actor: ActivityActor, options?: { onSuccess?: () => void }) {
  return useMutationWithToast({
    mutationFn: (id: string) => deactivateStaff(id, actor),
    successMessage: 'Staff member deactivated',
    invalidateKeys: [() => QK.staffMembers()],
    onSuccess: options?.onSuccess,
  })
}
